/**
 * Reusable creative-create dialog.
 *
 * Two modes share the same form body:
 *   - "brief" : strategy + slot are pre-set (slot drives type/funnel; both readonly)
 *   - "adhoc" : user picks strategy first; type/funnel/title/etc are all free
 *
 * Once a strategy is selected, we fetch the client to derive the per-vertical
 * taxonomy labels + value lists + talents + default channels.
 */
import { useEffect, useRef, useState } from 'react';
import { FileName, FolderPath } from '@/components/ui/FileName';
import { isDesign } from '@/lib/steps';
import { useQuery } from '@tanstack/react-query';
import { X, Sparkles, Layers, Paperclip, Save, Send, Link2, Plus, ExternalLink, FolderOpen, Folder } from 'lucide-react';
import { filesFromDataTransfer, droppedFolder, droppedFolders } from '@/lib/dropped';
import {
  qk, fetchStrategies, fetchClient, fetchStrategy,
  type CreativeCreateBody, type Strategy,
} from '@/lib/queries';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { CHANNELS_DEFAULT, CATEGORIES, AD_PLATFORMS, CREATIVE_TYPES } from '@shared/enums';
import { cn } from '@/lib/cn';
import RichEditor from '@/components/ui/RichEditor';
import { htmlToPlain } from '@/components/ui/RichView';

type Mode = 'brief' | 'adhoc';

/** Types whose tracker has a script (or concept) step: track.VIDEO_LIKE. */
const creativeTypeHasScript = (t: string) => t === 'video' || t === 'reel' || t === 'photo';

interface Props {
  mode: Mode;
  // brief mode: caller supplies these
  strategyId?: string;
  slot?: { slot_id: string; funnel: string; type: string };
  // adhoc mode: optionally pre-fill the strategy (e.g. opened from a strategy detail page)
  defaultStrategyId?: string;
  onClose: () => void;
  // The caller is responsible for POSTing the creative + uploading the chosen references.
  onSubmit: (body: CreativeCreateBody, referenceFiles: File[]) => void;
  submitting: boolean;
}

const FUNNELS = [
  { value: 'TOF', label: 'TOF (top of funnel)' },
  { value: 'MOF', label: 'MOF (middle)' },
  { value: 'BOF', label: 'BOF (bottom)' },
];

export default function CreativeFormDialog(p: Props) {
  // --- Strategy + client wiring ---
  const isBrief = p.mode === 'brief';
  const [strategyId, setStrategyId] = useState<string>(p.strategyId ?? p.defaultStrategyId ?? '');

  const strategies = useQuery({
    queryKey: qk.strategies, queryFn: fetchStrategies,
    enabled: !isBrief,  // brief mode already knows the strategy
  });
  const strategy = useQuery({
    queryKey: qk.strategy(strategyId),
    queryFn: () => fetchStrategy(strategyId),
    enabled: !!strategyId && !isBrief,
  });
  const clientId = strategy.data?.client_id;
  const client = useQuery({
    queryKey: qk.client(clientId ?? ''),
    queryFn: () => fetchClient(clientId!),
    enabled: !!clientId,
  });

  // --- Form state ---
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  // The content creator writes the script while creating the piece (Aziz, 2026-09-24),
  // instead of hunting for it on the shoot card afterwards.
  const [script, setScript] = useState('');
  const [scriptHtml, setScriptHtml] = useState('');
  const [noDoctor, setNoDoctor] = useState(false);
  const [shootNotes, setShootNotes] = useState('');
  const [creativeType, setCreativeType] = useState<string>(p.slot?.type ?? 'reel');
  const [funnel, setFunnel] = useState<string>(p.slot?.funnel ?? 'TOF');
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [talent, setTalent] = useState('');
  const [channels, setChannels] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isPaid, setIsPaid] = useState(false);
  // Video, reel and photo: the person must SAY whether there is a shoot. Aziz 2026-10-16: a
  // reel saved on the old "needs a shoot" default went to the videographer when it was made
  // from existing material (MS-10-2026-010). So nothing is pre-picked for these types and the
  // form will not save until one is chosen. The static families keep the photoshoot box, off.
  const shootByType = (t: string) => t === 'video' || t === 'reel' || t === 'photo';
  const [needsShoot, setNeedsShoot] = useState<boolean | null>(() => (shootByType(p.slot?.type ?? 'reel') ? null : false));
  const [needsShootTouched, setNeedsShootTouched] = useState(false);
  useEffect(() => { if (!needsShootTouched) setNeedsShoot(shootByType(creativeType) ? null : false); }, [creativeType]);   // eslint-disable-line react-hooks/exhaustive-deps
  const chooseShoot = (v: boolean) => { setNeedsShoot(v); setNeedsShootTouched(true); };
  const [adPlatforms, setAdPlatforms] = useState<string[]>([]);
  const [executionDate, setExecutionDate] = useState('');
  const [postingDate, setPostingDate] = useState('');
  const [refs, setRefs] = useState<File[]>([]);
  const refInputRef = useRef<HTMLInputElement | null>(null);
  const [refUrls, setRefUrls] = useState<string[]>([]);
  const [refUrlDraft, setRefUrlDraft] = useState('');

  // Once client loads, default channels for that client
  useEffect(() => {
    if (client.data && channels.length === 0) {
      setChannels(client.data.default_channels.length ? client.data.default_channels : ['instagram', 'tiktok']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.data]);

  function toggle<T>(arr: T[], v: T, set: (n: T[]) => void) {
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  const taxonomy = client.data?.taxonomy;
  const talents = client.data?.talents ?? [];

  function submit(asSubmit: boolean) {
    if (!strategyId || !title.trim()) return;
    const talentObj = talents.find((t) => t.id === talent);
    p.onSubmit({
      strategy_id: strategyId,
      slot_id: p.slot?.slot_id,
      title: title.trim(),
      description: description.trim() || undefined,
      description_html: descriptionHtml || undefined,
      script: creativeTypeHasScript(creativeType) ? (script.trim() || undefined) : undefined,
      script_html: creativeTypeHasScript(creativeType) ? (scriptHtml || undefined) : undefined,
      creative_type: creativeType as CreativeCreateBody['creative_type'],
      funnel_stage: funnel as CreativeCreateBody['funnel_stage'],
      categories,
      is_paid: isPaid,
      needs_shoot: needsShoot ?? undefined,
      ad_platforms: isPaid ? adPlatforms : [],
      taxonomy_primary: primary || undefined,
      taxonomy_secondary: secondary || undefined,
      talent_id: noDoctor ? undefined : (talent || undefined),
      talent_name: noDoctor ? undefined : talentObj?.name,
      script_needs_doctor: creativeTypeHasScript(creativeType) ? !noDoctor : undefined,
      shoot_notes: creativeTypeHasScript(creativeType) && needsShoot ? (shootNotes.trim() || undefined) : undefined,
      channels,
      execution_date: executionDate || undefined,
      posting_date: postingDate || undefined,
      // Include a link the user typed but did not press Enter on, so a brief is
      // never silently submitted without the reference sitting in the box.
      reference_urls: addRefUrl(refUrlDraft, refUrls),
      submit: asSubmit,
    }, refs);
  }

  /** Append `raw` to `list`, normalised and de-duped. Returns a new array.
   *  Mirror of the server's allowlist (schemas._clean_reference_urls) - kept in
   *  step so the field never shows a chip the API will silently drop. Scheme is
   *  parsed properly: testing for "://" misses `javascript:alert(1)`. */
  function addRefUrl(raw: string, list: string[]): string[] {
    const u = raw.trim();
    if (!u || u.length > 2000 || list.length >= 10) return list;
    const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(u);
    let normalised = u;
    if (m) {
      const scheme = m[1].toLowerCase();
      if (scheme !== 'http' && scheme !== 'https') {
        // "localhost:3000/x" is a host:port, not a foreign scheme.
        const rest = u.slice(m[0].length).split('/', 1)[0];
        if (!/^\d+$/.test(rest)) return list;
        normalised = `https://${u}`;
      }
    } else {
      normalised = `https://${u}`;
    }
    if (list.includes(normalised)) return list;
    return [...list, normalised];
  }
  function commitRefUrl() {
    const next = addRefUrl(refUrlDraft, refUrls);
    if (next !== refUrls) setRefUrls(next);
    setRefUrlDraft('');
  }
  function removeRefUrl(idx: number) {
    setRefUrls((cur) => cur.filter((_, i) => i !== idx));
  }

  // Folders keep their names: each file remembers the folder it came from (lib/dropped)
  // and is filed under it on the creative, so four dropped folders arrive as four
  // reference folders, not one flat pile (MS-10-2026-011, 2026-10-15).
  function pickRefs(files: FileList | File[] | null) {
    if (!files || !files.length) return;
    setRefs((cur) => [...cur, ...Array.from(files)]);
  }
  const refDirRef = useRef<HTMLInputElement>(null);
  function removeRef(idx: number) {
    setRefs((cur) => cur.filter((_, i) => i !== idx));
  }

  const canSubmit = !!strategyId && !!title.trim() && !!creativeType && !!funnel && needsShoot !== null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-bg-card border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
              isBrief ? 'bg-accent-600/20 text-accent-400' : 'bg-emerald-600/20 text-emerald-400',
            )}>
              {isBrief ? <Layers size={18} /> : <Sparkles size={18} />}
            </div>
            <div>
              <h3 className="font-medium text-muted-strong">New creative</h3>
              <p className="text-xs text-muted">
                {isBrief
                  ? `From planned slot - ${p.slot?.funnel} - ${p.slot?.type}`
                  : 'Pick a strategy, write the brief, attach references.'}
              </p>
            </div>
          </div>
          <button onClick={p.onClose} className="text-muted hover:text-muted-strong"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Strategy picker (adhoc only) */}
          {!isBrief && (
            <Select
              label="Strategy"
              value={strategyId}
              onChange={(v) => { setStrategyId(v); setPrimary(''); setSecondary(''); setTalent(''); setChannels([]); }}
              searchable
              placeholder="Pick a strategy..."
              options={(strategies.data ?? []).map((s: Strategy) => ({ value: s.id, label: s.title, hint: `${s.code} - ${s.client_name}` }))}
            />
          )}

          {/* Type + funnel (ad-hoc only - in brief mode they're locked to the slot) */}
          {!isBrief && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select
                label="Creative type"
                value={creativeType}
                onChange={setCreativeType}
                options={CREATIVE_TYPES.map((t) => ({ value: t, label: t }))}
              />
              <Select
                label="Funnel stage"
                value={funnel}
                onChange={setFunnel}
                options={FUNNELS}
              />
            </div>
          )}

          {/* The form is always visible. Only the clinic-specific tags wait for
              the strategy (they come from the client's taxonomy). Found by the
              journey test: an empty dialog with one dropdown read as broken. */}
          {(isBrief ? !!client.data : true) ? (
            <>
              <Input label="Working title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Endo Lift - Instant Youth" />

              {shootByType(creativeType) ? (
                <div className="space-y-1.5 text-sm" data-testid="needs-shoot">
                  <span className="text-muted-strong">Shoot</span>
                  <div className="flex flex-wrap gap-2">
                    {([[true, 'Needs a shoot', 'needs-shoot-yes'], [false, 'No shoot, existing material', 'needs-shoot-no']] as const).map(([v, label, tid]) => (
                      <button key={tid} type="button" onClick={() => chooseShoot(v)} aria-pressed={needsShoot === v} data-testid={tid}
                        className={`px-3 py-1.5 rounded-md border text-xs ${needsShoot === v ? 'border-accent-400 bg-accent-400/15 text-accent-300' : 'border-slate-700 text-muted hover:border-slate-500'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <span className="block text-xs text-muted" data-testid="needs-shoot-hint">{needsShoot === null
                    ? 'Choose one before saving. A filmed piece goes to the videographer; existing material goes straight to the video editor.'
                    : needsShoot ? 'Filmed on a shoot day: shoot, raws and the raws review are in the plan.' : 'Made from existing material: no shoot, straight to the video editor once the script is approved.'}</span>
                </div>
              ) : (
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="needs-shoot">
                <input type="checkbox" checked={!!needsShoot} onChange={(e) => chooseShoot(e.target.checked)} className="accent-accent-400 mt-1" data-testid="needs-shoot-input" />
                <span>
                  <span className="text-muted-strong">{isDesign(creativeType) ? 'Photoshoot needed' : 'Needs a shoot'}</span>
                  <span className="block text-xs text-muted">{isDesign(creativeType)
                    ? (needsShoot ? 'Photographed first: photoshoot, raws and the art director\'s raws review, then the design.' : 'Designed from what exists: one design step, then the review.')
                    : (needsShoot ? 'Filmed on a shoot day: shoot, raws and the raws review are in the plan.' : 'Made from existing material: no shoot, straight to the editor once the script is approved.')}</span>
                </span>
              </label>
              )}

              {/* Who and what it is about. Always shown, so nothing looks missing; the lists
                  come from the clinic, so they wait for the strategy (Aziz, 2026-09-24:
                  "why doctor and treatment are not there"). */}
              <div className="space-y-3" data-testid="create-clinic-fields">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Select
                    label={taxonomy?.primary.label ?? 'Specialty'}
                    value={primary}
                    onChange={setPrimary}
                    placeholder={client.data ? '(any)' : 'Pick a strategy first'}
                    disabled={!client.data}
                    options={!client.data ? [] : [
                      { value: '', label: '(any)' },
                      ...(taxonomy?.primary.values ?? []).map((v) => ({ value: v, label: v })),
                    ]}
                  />
                  <Select
                    label={taxonomy?.secondary.label ?? 'Treatment'}
                    value={secondary}
                    onChange={setSecondary}
                    placeholder={client.data ? '(any)' : 'Pick a strategy first'}
                    disabled={!client.data}
                    options={!client.data ? [] : [
                      { value: '', label: '(any)' },
                      ...(taxonomy?.secondary.values ?? []).map((v) => ({ value: v, label: v })),
                    ]}
                  />
                </div>

                {(!client.data || taxonomy?.talent.enabled) && (
                  <div className="space-y-2">
                    <Select
                      label={taxonomy?.talent.label ?? 'Doctor'}
                      value={noDoctor ? '' : talent}
                      onChange={setTalent}
                      placeholder={!client.data ? 'Pick a strategy first' : noDoctor ? 'No doctor on this one' : '(none yet)'}
                      disabled={!client.data || noDoctor}
                      searchable
                      options={!client.data || noDoctor ? [] : [
                        { value: '', label: '(none yet)' },
                        ...talents.map((t) => ({ value: t.id, label: t.name })),
                      ]}
                    />
                    {creativeTypeHasScript(creativeType) && (
                      <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="create-no-doctor">
                        <input type="checkbox" checked={noDoctor} onChange={(e) => { setNoDoctor(e.target.checked); if (e.target.checked) setTalent(''); }}
                          className="accent-accent-400 mt-1" data-testid="create-no-doctor-input" />
                        <span>
                          <span className="text-muted-strong">No doctor on this one</span>
                          <span className="block text-xs text-muted">A coordinator piece or a general clinic video. Nobody approves the script.</span>
                        </span>
                      </label>
                    )}
                  </div>
                )}
                {!client.data && !isBrief && (
                  <p className="text-[11px] text-muted">
                    {strategyId ? "Loading the clinic's specialties, treatments and doctors..." : "Pick a strategy above: the specialties, treatments, doctors and channels are the clinic's own."}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5 text-sm">
                <span className="text-xs text-muted">Brief / concept</span>
                <RichEditor html={descriptionHtml} plain={description} minHeight={80} testid="create-brief"
                  onChange={(h) => { setDescriptionHtml(h); setDescription(htmlToPlain(h)); }}
                  placeholder="What is this creative for? Hook, angle, key message..." />
              </div>

              {creativeTypeHasScript(creativeType) && (
                <div className="flex flex-col gap-1.5 text-sm" data-testid="create-script">
                  <span className="text-xs text-muted">{creativeType === 'photo' ? 'Concept' : 'Script'} <span className="text-muted/70">{noDoctor ? '(optional now)' : '(optional now, the doctor approves it later)'}</span></span>
                  <RichEditor html={scriptHtml} plain={script} minHeight={140} testid="create-script-input"
                    onChange={(h) => { setScriptHtml(h); setScript(htmlToPlain(h)); }}
                    placeholder={creativeType === 'photo' ? 'What the shots are, in order...' : 'What is said and shown, scene by scene...'} />
                </div>
              )}

              {creativeTypeHasScript(creativeType) && needsShoot && (
                <label className="flex flex-col gap-1.5 text-sm" data-testid="create-shoot-notes">
                  <span className="text-xs text-muted">Notes for the crew</span>
                  <textarea value={shootNotes} onChange={(e) => setShootNotes(e.target.value)} rows={2} dir="auto" maxLength={4000}
                    className="rounded-lg border border-slate-800 bg-bg px-3 py-2 text-sm text-muted-strong focus:outline-none focus:border-accent-400"
                    placeholder="Wardrobe, props, patient consent, parking..." data-testid="create-shoot-notes-input" />
                </label>
              )}


              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Execution date (shoot/design)" type="date" value={executionDate} onChange={(e) => setExecutionDate(e.target.value)} />
                <Input label="Posting date" type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} />
              </div>

              {/* References - mood-board / look-like-this files. Uploaded as kind=reference on the
                  newly-created creative (held in browser memory until submit). */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted">References (mood-board, "make it look like this") · files or links</label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); void filesFromDataTransfer(e.dataTransfer).then(pickRefs); }}
                  data-testid="ref-dropzone"
                  className="rounded-lg border border-dashed border-slate-700 hover:border-accent-400/50 p-3 transition"
                >
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={<Paperclip size={12} />}
                      onClick={() => refInputRef.current?.click()}
                    >
                      Attach files
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={<FolderOpen size={12} />}
                      onClick={() => refDirRef.current?.click()}
                      data-testid="ref-pick-folder"
                    >
                      Attach a folder
                    </Button>
                    <span className="text-[11px] text-muted">or drop files or whole folders here · images, video, PDFs, Word</span>
                    <input
                      ref={refInputRef}
                      type="file"
                      multiple
                      hidden
                      data-testid="ref-file-input"
                      onChange={(e) => { pickRefs(Array.from(e.target.files ?? [])); e.currentTarget.value = ''; }}
                    />
                    <input
                      ref={refDirRef}
                      type="file"
                      multiple
                      hidden
                      data-testid="ref-folder-input"
                      {...({ webkitdirectory: '' } as Record<string, string>)}
                      onChange={(e) => { pickRefs(Array.from(e.target.files ?? [])); e.currentTarget.value = ''; }}
                    />
                  </div>

                  {/* Link half of the same idea: an IG reel, a TikTok, a Drive folder,
                      a Pinterest board. Enter (or the + button) commits one. */}
                  <div className="mt-3 flex items-center gap-2">
                    <div className="relative flex-1">
                      <Link2 size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                      <input
                        type="url"
                        inputMode="url"
                        value={refUrlDraft}
                        onChange={(e) => setRefUrlDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRefUrl(); }
                        }}
                        onBlur={commitRefUrl}
                        disabled={refUrls.length >= 10}
                        placeholder="Paste a link - instagram.com/reel/... , a Drive folder, a Pinterest board"
                        className="w-full bg-bg-input border border-slate-700 rounded-lg pl-7 pr-2 py-1.5 text-xs text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400/50 disabled:opacity-50"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={<Plus size={12} />}
                      onClick={commitRefUrl}
                      disabled={!refUrlDraft.trim() || refUrls.length >= 10}
                    >
                      Add link
                    </Button>
                  </div>
                  {refUrls.length >= 10 && (
                    <p className="mt-1.5 text-[11px] text-muted">Maximum of 10 links reached.</p>
                  )}

                  {refUrls.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {refUrls.map((u, i) => (
                        <li key={u} className="flex items-center gap-2 text-[11px] bg-slate-800/60 rounded-lg px-2 py-1.5">
                          <ExternalLink size={11} className="text-muted shrink-0" />
                          <a
                            href={u}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex-1 truncate text-accent-400 hover:underline"
                            title={u}
                          >
                            {u.replace(/^https?:\/\//, '')}
                          </a>
                          <button
                            type="button"
                            onClick={() => removeRefUrl(i)}
                            className="text-muted hover:text-red-400 shrink-0"
                            aria-label={`Remove ${u}`}
                          >
                            <X size={11} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {refs.length > 0 && droppedFolders(refs).length > 0 && (
                    <p className="mt-3 text-[11px] text-muted" data-testid="ref-folders-note">
                      {droppedFolders(refs).length} folder{droppedFolders(refs).length === 1 ? '' : 's'} kept: {droppedFolders(refs).map((d) => d.split('/').join(' / ')).join(', ')}
                    </p>
                  )}
                  {refs.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {refs.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-slate-800 text-muted-strong" data-testid="ref-chip">
                          {droppedFolder(f) ? <Folder size={10} className="opacity-60" /> : <Paperclip size={10} className="opacity-60" />}
                          {droppedFolder(f) && <span className="max-w-[120px] truncate text-muted"><FolderPath path={droppedFolder(f)} /> /</span>}
                          <FileName name={f.name} className="inline-block max-w-[200px] truncate align-bottom" />
                          <button
                            type="button"
                            onClick={() => removeRef(i)}
                            className="text-muted hover:text-red-400 -mr-0.5"
                          >
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Multi label="Channels" options={CHANNELS_DEFAULT as unknown as string[]} value={channels} onToggle={(v) => toggle(channels, v, setChannels)} />
              <Multi label="Categories (multi-select)" options={CATEGORIES as unknown as string[]} value={categories} onToggle={(v) => toggle(categories, v, setCategories)} />

              <label className="flex items-center gap-2 text-sm text-muted-strong">
                <input type="checkbox" checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} className="accent-accent-400" />
                Paid distribution
              </label>
              {isPaid && (
                <Multi label="Ad platforms" options={AD_PLATFORMS as unknown as string[]} value={adPlatforms} onToggle={(v) => toggle(adPlatforms, v, setAdPlatforms)} />
              )}
            </>
          ) : (
            <div className="py-8 text-center text-sm text-muted">
              {!strategyId ? 'Pick a strategy to continue.' : 'Loading client info...'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-5 border-t border-slate-800">
          <Button variant="ghost" onClick={p.onClose}>Cancel</Button>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              icon={<Save size={14} />}
              loading={p.submitting}
              disabled={!canSubmit}
              onClick={() => submit(false)}
              title="Save without alerting the team - keep editing later"
            >
              Save as draft
            </Button>
            <Button
              icon={<Send size={14} />}
              loading={p.submitting}
              disabled={!canSubmit}
              onClick={() => submit(true)}
              title="Hand off to the production team - they'll be notified"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Multi({
  label, options, value, onToggle,
}: { label: string; options: string[]; value: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-muted">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onToggle(o)}
            className={cn(
              'text-[11px] px-2.5 py-1 rounded-full border transition',
              value.includes(o)
                ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                : 'border-slate-800 text-muted hover:text-muted-strong'
            )}>
            {o.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
    </div>
  );
}
